/**
 * motion.ts — Central motion tokens for TruePrice.
 *
 * All stepper-driven animations share these configs.
 * Import here instead of inlining spring configs per-component.
 */

/** Spring config for all stepper-driven animations (bar widths, dot position, number rolls).
 *  Slightly overdamped — settles in ~240ms, no bounce. */
export const STEPPER_SPRING = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 30,
  mass: 0.8,
};

/** CSS easing string equivalent of STEPPER_SPRING for use in CSS transition properties. */
export const STEPPER_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

/** Duration (ms) used for CSS transitions matching STEPPER_SPRING settle time. */
export const STEPPER_MS = 240;

/** Crossfade config for text/color changes (authority sentence, zone color). */
export const CROSSFADE = {
  duration: 0.2,
  ease: [0.32, 0.72, 0, 1] as [number, number, number, number],
};

/** Pulse config for zone-crossing emphasis on the corridor bar. */
export const ZONE_PULSE = {
  scale: [1, 1.04, 1] as [number, number, number],
  transition: { duration: 0.28, ease: 'easeInOut' as const },
};

/** Returns false when the user prefers reduced motion — skip all animations. */
export function shouldAnimate(): boolean {
  if (typeof window === 'undefined') return false;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** CSS transition shorthand for position properties (left, width). */
export function stepperTransition(props: string | string[] = 'left'): string {
  const propList = Array.isArray(props) ? props : [props];
  return propList
    .map((p) => `${p} ${STEPPER_MS}ms ${STEPPER_EASE}`)
    .join(', ');
}

/** CSS transition shorthand for color properties. */
export const COLOR_TRANSITION = `background-color 200ms ease, box-shadow 200ms ease, color 200ms ease`;
